/**
 * Ambient Particles
 *
 * Draws slow-floating shapes on a canvas behind the header intro copy.
 * Purely decorative, so it stays out of the accessibility tree, respects
 * prefers-reduced-motion, pauses off-screen/hidden, and tracks dark-mode
 * colour changes.
 *
 * By default it draws simple procedural shapes (circles, squares,
 * triangles, rings) tinted with the site's accent colours. You can swap
 * those out for your own images/SVGs instead, either by passing them in:
 *
 *   initParticles({ images: ['assets/img/particles/star.svg', 'assets/img/particles/blob.svg'] });
 *
 * ...or, without touching the JS, by listing them on the canvas element:
 *
 *   <canvas class="z-particles" data-particle-images="assets/img/particles/star.svg, assets/img/particles/blob.svg"></canvas>
 *
 * When images are supplied they replace the procedural shapes entirely,
 * unless `mixShapes: true` is passed, in which case both are drawn
 * together. Images are drawn with their natural aspect ratio preserved and
 * their own baked-in colours by default (only their opacity is animated);
 * while Halloween mode is active they're re-tinted to the Halloween colour
 * palette, same as the procedural shapes.
 *
 * Particles also treat the intro copy as solid obstacles: by default the
 * bounding box of each direct child of `.z-intro .z-container` (the
 * homepage heading, lead paragraph, and requests line) or
 * `.z-project.--intro .z-container` (the project-page heading, lead
 * paragraph, and services line) is bounced off of like a wall, so shapes
 * drift around the text instead of drifting behind it.
 * Override which elements count as obstacles with
 * `initParticles({ obstacleSelector: '.my-selector' })`, via
 * `data-particle-obstacles="…"` on the canvas, or disable the behaviour
 * entirely with `initParticles({ obstacles: false })`.
 */

const SHAPES = ['circle', 'square', 'triangle', 'ring'];
// These read from dedicated --particle-color-* custom properties (rather
// than --green/--blue/etc. directly) so theme classes like .halloween-mode
// can retint just the particles without affecting other UI colours.
const COLOR_VARS = ['--particle-color-1', '--particle-color-2', '--particle-color-3', '--particle-color-4'];
const DENSITY = 1 / 18000; // ~1 particle per 18,000px^2 of canvas
const MAX_PARTICLES = 22;
const MIN_PARTICLES = 8;

// Viewport widths (in CSS px) used to decide which PARTICLE_LIMITS tier
// applies: <= `mobile` is a phone, <= `tablet` is a tablet, anything wider
// is treated as desktop. Both the widths and the tier limits below can be
// overridden via initParticles({ breakpoints, particleLimits }).
const DEFAULT_BREAKPOINTS = {
    mobile: 576,
    tablet: 992,
};

// Fewer/smaller particle counts for touch-sized viewports: mobile and
// tablet screens get a lower density plus lower min/max counts so the
// effect stays lightweight on smaller, often lower-powered devices.
const DEFAULT_PARTICLE_LIMITS = {
    mobile: { density: DENSITY * 0.5, min: 4, max: 8 },
    tablet: { density: DENSITY * 0.7, min: 6, max: 14 },
    desktop: { density: DENSITY, min: MIN_PARTICLES, max: MAX_PARTICLES },
};

// How the initial batch of particles pops in on page load: each one waits
// its turn (staggered a few frames apart, plus a little jitter) then scales
// and fades up over APPEAR_FRAMES frames.
const APPEAR_STAGGER_FRAMES = 4;
const APPEAR_STAGGER_JITTER = 4;
const APPEAR_FRAMES = 24;

// How strongly particles are pushed away from the cursor: anything within
// REPEL_RADIUS (in CSS px) gets nudged away each frame, with the push
// fading out to nothing at the edge of the radius.
const REPEL_RADIUS = 120;
const REPEL_STRENGTH = 2.4;

// While the window is actively being resized, particles shrink away to
// nothing over SHRINK_FRAMES frames; once resize events stop for
// RESIZE_SETTLE_MS, a fresh batch is rebuilt and pops back in the same way
// the initial page-load batch does.
const SHRINK_FRAMES = 14;
const RESIZE_SETTLE_MS = 180;

// Default elements whose bounding boxes particles bounce off of, so the
// intro copy reads as solid ground rather than something particles drift
// behind. Covers both the homepage hero (`.z-intro`) and project-page
// intros (`.z-project.--intro`). Can be overridden via
// `initParticles({ obstacleSelector })` or `data-particle-obstacles` on the
// canvas.
const DEFAULT_OBSTACLE_SELECTOR = '.z-intro .z-container > *, .z-project.--intro .z-container > *';

const randomBetween = (min, max) => min + Math.random() * (max - min);

// Overshoots slightly past 1 before settling, giving the entrance a gentle "pop".
const easeOutBack = (x) => {
    const c1 = 1.70158;
    const c3 = c1 + 1;
    return 1 + c3 * (x - 1) ** 3 + c1 * (x - 1) ** 2;
};

const easeOutCubic = (x) => 1 - (1 - x) ** 3;
const easeInCubic = (x) => x * x * x;

/**
 * Loads an image (raster or SVG) and resolves with the element, or `null`
 * if it fails to load, so a bad path never breaks the whole effect.
 */
const loadImage = (src) => new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
});

const loadImages = async (sources) => {
    const loaded = await Promise.all(sources.map(loadImage));
    return loaded.filter(Boolean);
};

const createParticle = (width, height, images) => {
    const size = randomBetween(16, 26);
    const angle = randomBetween(0, Math.PI * 2);
    const speed = randomBetween(0.05, 0.18);
    const useImage = images.length > 0 && (images.sourceOnly || Math.random() < images.ratio);

    const particle = {
        x: randomBetween(0, width),
        y: randomBetween(0, height),
        size,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rotation: randomBetween(.01, .1),
        rotationSpeed: randomBetween(-0.015, 0.030),
        opacity: 1,
        colorIndex: Math.floor(Math.random() * COLOR_VARS.length),
        // Fully "appeared" by default; buildParticles() resets these on the
        // initial load so particles pop in one by one instead of all at once.
        appearDelay: 0,
        appearProgress: 1,
    };

    if (useImage) {
        const image = images[Math.floor(Math.random() * images.length)];
        particle.shape = 'image';
        particle.image = image;
        particle.aspect = image.naturalWidth && image.naturalHeight
            ? image.naturalWidth / image.naturalHeight
            : 1;
    } else {
        particle.shape = SHAPES[Math.floor(Math.random() * SHAPES.length)];
    }

    return particle;
};

const drawShape = (ctx, particle, tintColor) => {
    const { shape, size } = particle;

    if (shape === 'image') {
        const w = particle.aspect >= 1 ? size : size * particle.aspect;
        const h = particle.aspect >= 1 ? size / particle.aspect : size;
        ctx.drawImage(particle.image, -w / 2, -h / 2, w, h);

        // Images keep their own baked-in colours by default. When a tint
        // colour is supplied (e.g. Halloween mode), re-colour the opaque
        // pixels by compositing a flat fill "source-atop" the image.
        if (tintColor) {
            ctx.globalCompositeOperation = 'source-atop';
            ctx.fillStyle = tintColor;
            ctx.fillRect(-w / 2, -h / 2, w, h);
            ctx.globalCompositeOperation = 'source-over';
        }
        return;
    }

    ctx.beginPath();

    if (shape === 'circle') {
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.fill();
    } else if (shape === 'ring') {
        ctx.arc(0, 0, size / 2, 0, Math.PI * 2);
        ctx.lineWidth = Math.max(1.5, size / 10);
        ctx.stroke();
    } else if (shape === 'square') {
        ctx.rect(-size / 2, -size / 2, size, size);
        ctx.fill();
    } else if (shape === 'triangle') {
        const half = size / 2;
        ctx.moveTo(0, -half);
        ctx.lineTo(half, half);
        ctx.lineTo(-half, half);
        ctx.closePath();
        ctx.fill();
    }
};

/**
 * Bounces a particle off an obstacle rectangle (e.g. a line of intro copy)
 * it has overlapped, treating the rect as solid ground: the particle is
 * pushed back out along whichever edge it penetrated least, and the
 * matching velocity component is flipped so it reflects away instead of
 * drifting through.
 */
const resolveObstacleCollision = (particle, rect) => {
    const radius = particle.size / 2;
    const left = rect.left - radius;
    const right = rect.right + radius;
    const top = rect.top - radius;
    const bottom = rect.bottom + radius;

    if (particle.x < left || particle.x > right || particle.y < top || particle.y > bottom) return;

    const penLeft = particle.x - left;
    const penRight = right - particle.x;
    const penTop = particle.y - top;
    const penBottom = bottom - particle.y;
    const minPen = Math.min(penLeft, penRight, penTop, penBottom);

    if (minPen === penLeft) {
        particle.x = left;
        particle.vx = -Math.abs(particle.vx);
    } else if (minPen === penRight) {
        particle.x = right;
        particle.vx = Math.abs(particle.vx);
    } else if (minPen === penTop) {
        particle.y = top;
        particle.vy = -Math.abs(particle.vy);
    } else {
        particle.y = bottom;
        particle.vy = Math.abs(particle.vy);
    }
};

/**
 * @param {Object} [options]
 * @param {string[]} [options.images] Custom image/SVG URLs to float instead
 *   of (or alongside) the procedural shapes.
 * @param {boolean} [options.mixShapes] When `images` is set, also draw the
 *   procedural shapes alongside the images instead of replacing them.
 * @param {string} [options.obstacleSelector] CSS selector matching elements
 *   whose bounding boxes particles should bounce off of (defaults to the
 *   intro heading/lead/requests line on the homepage, plus the
 *   heading/lead/services line on project pages). Overridable per-canvas
 *   via `data-particle-obstacles`.
 * @param {boolean} [options.obstacles] Set to `false` to disable bouncing
 *   off of text obstacles entirely.
 * @param {Object} [options.breakpoints] Viewport widths (CSS px) deciding
 *   which `particleLimits` tier applies. Defaults to
 *   `{ mobile: 576, tablet: 992 }`: viewports up to `mobile` wide use the
 *   `mobile` tier, up to `tablet` wide use the `tablet` tier, and anything
 *   wider uses `desktop`.
 * @param {Object} [options.particleLimits] Per-tier `{ density, min, max }`
 *   overrides (merged over the defaults below) controlling how many
 *   particles are generated at each breakpoint tier. `density` is particles
 *   per CSS px^2 of canvas area; `min`/`max` clamp the resulting count.
 *   Defaults are lower for `mobile`/`tablet` than `desktop` so fewer
 *   particles render on smaller devices.
 */
const initParticles = async (options = {}) => {
    const canvas = document.querySelector('.z-particles');
    if (!canvas || !canvas.getContext) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (prefersReducedMotion.matches) return;

    const datasetSources = (canvas.dataset.particleImages || '')
        .split(',')
        .map((src) => src.trim())
        .filter(Boolean);
    const imageSources = options.images && options.images.length ? options.images : datasetSources;
    const mixShapes = Boolean(options.mixShapes);

    const obstaclesEnabled = options.obstacles !== false;
    const obstacleSelector = options.obstacleSelector || canvas.dataset.particleObstacles || DEFAULT_OBSTACLE_SELECTOR;
    const obstacleElements = obstaclesEnabled ? Array.from(document.querySelectorAll(obstacleSelector)) : [];

    const breakpoints = { ...DEFAULT_BREAKPOINTS, ...options.breakpoints };
    const particleLimits = {
        mobile: { ...DEFAULT_PARTICLE_LIMITS.mobile, ...options.particleLimits?.mobile },
        tablet: { ...DEFAULT_PARTICLE_LIMITS.tablet, ...options.particleLimits?.tablet },
        desktop: { ...DEFAULT_PARTICLE_LIMITS.desktop, ...options.particleLimits?.desktop },
    };

    // Picks the particle-count tier for the current viewport width so
    // mobile/tablet devices render fewer particles than desktop.
    const getParticleLimits = () => {
        const viewportWidth = window.innerWidth;
        if (viewportWidth <= breakpoints.mobile) return particleLimits.mobile;
        if (viewportWidth <= breakpoints.tablet) return particleLimits.tablet;
        return particleLimits.desktop;
    };

    const loadedImages = imageSources.length ? await loadImages(imageSources) : [];
    // `ratio` is the chance any given particle uses an image rather than a
    // shape; `sourceOnly` forces every particle to use one when not mixing.
    loadedImages.ratio = mixShapes ? 0.5 : 1;
    loadedImages.sourceOnly = loadedImages.length > 0 && !mixShapes;

    const ctx = canvas.getContext('2d');
    const container = canvas.parentElement;

    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let colors = [];
    let halloweenActive = false;
    let animationFrame = null;
    let isVisible = true;

    // Drives the shrink-away/pop-back-in transition while the window is
    // being resized: 1 = full size, 0 = fully shrunk. `isShrinking` tracks
    // whether it should currently be counting down, and `awaitingRebuild`
    // marks that resize events have stopped and a rebuild is due as soon as
    // the shrink reaches 0.
    let shrinkProgress = 1;
    let isShrinking = false;
    let awaitingRebuild = false;
    let resizeSettleTimer = null;

    // Mouse/touch position in the same local (CSS px) coordinate space as
    // the particles, used to push nearby particles away each frame.
    const pointer = { x: 0, y: 0, active: false };

    const readColors = () => {
        const styles = getComputedStyle(document.documentElement);
        colors = COLOR_VARS.map((name) => styles.getPropertyValue(name).trim() || '#2F3035');
        halloweenActive = document.documentElement.classList.contains('halloween-mode');
    };

    // `animateIn` staggers each particle's opacity/scale from 0 so they pop
    // in one by one; it's only true for the very first build, so resizing
    // the window later just repositions particles instantly as before.
    const buildParticles = (animateIn) => {
        const limits = getParticleLimits();
        const target = Math.round(width * height * limits.density);
        const count = Math.min(limits.max, Math.max(limits.min, target));
        particles = Array.from({ length: count }, (_, index) => {
            const particle = createParticle(width, height, loadedImages);
            if (animateIn) {
                particle.appearDelay = index * APPEAR_STAGGER_FRAMES + Math.floor(randomBetween(0, APPEAR_STAGGER_JITTER));
                particle.appearProgress = 0;
            }
            return particle;
        });
    };

    // Keeps the canvas's backing resolution in sync with its on-screen size
    // (so it never looks stretched/blurry), without touching the particle
    // list itself.
    const updateCanvasSize = () => {
        const rect = container.getBoundingClientRect();
        width = rect.width;
        height = rect.height;
        dpr = Math.min(window.devicePixelRatio || 1, 2);

        canvas.width = width * dpr;
        canvas.height = height * dpr;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    // Converts the obstacle elements' current viewport rects into the same
    // local (CSS px) coordinate space as the particles (mirroring the
    // pointer-position conversion below), so text that reflows/moves (on
    // resize, the intro's fade-in animation, etc.) stays accurately bounced
    // off of.
    const getObstacleRects = () => {
        if (!obstacleElements.length) return [];
        const containerRect = container.getBoundingClientRect();
        return obstacleElements.map((el) => {
            const rect = el.getBoundingClientRect();
            return {
                left: rect.left - containerRect.left,
                top: rect.top - containerRect.top,
                right: rect.right - containerRect.left,
                bottom: rect.bottom - containerRect.top,
            };
        }).filter((rect) => rect.right > rect.left && rect.bottom > rect.top);
    };

    const resize = (animateIn = false) => {
        updateCanvasSize();
        buildParticles(animateIn);
    };

    // Called on every `resize` event. Rather than rebuilding particles on
    // every tick of a drag-resize (which just snaps them to new random
    // spots), keep the canvas sized correctly but shrink the current batch
    // away; once resize events stop for RESIZE_SETTLE_MS, rebuild against
    // the final size and let the new batch pop back in like on page load.
    const handleResize = () => {
        updateCanvasSize();
        isShrinking = true;
        awaitingRebuild = false;

        if (resizeSettleTimer) clearTimeout(resizeSettleTimer);
        resizeSettleTimer = setTimeout(() => {
            awaitingRebuild = true;
            // If the shrink has already finished, rebuild immediately
            // instead of waiting for the next animation frame to notice.
            if (shrinkProgress <= 0) {
                buildParticles(true);
                isShrinking = false;
                awaitingRebuild = false;
                shrinkProgress = 1;
            }
        }, RESIZE_SETTLE_MS);
    };

    const step = () => {
        ctx.clearRect(0, 0, width, height);

        // Count the shrink-away transition down, then rebuild the particle
        // batch (with its own pop-back-in animation) once it reaches 0 and
        // resize events have settled.
        if (isShrinking) {
            shrinkProgress = Math.max(0, shrinkProgress - 1 / SHRINK_FRAMES);
            if (shrinkProgress <= 0 && awaitingRebuild) {
                buildParticles(true);
                isShrinking = false;
                awaitingRebuild = false;
                shrinkProgress = 1;
            }
        }
        const shrinkScale = shrinkProgress < 1 ? easeInCubic(shrinkProgress) : 1;
        const obstacles = getObstacleRects();

        particles.forEach((particle) => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.rotation += particle.rotationSpeed;

            // Push the particle away from the pointer, fading out toward
            // the edge of REPEL_RADIUS so the effect feels like a field
            // rather than a hard boundary.
            if (pointer.active) {
                const dx = particle.x - pointer.x;
                const dy = particle.y - pointer.y;
                const distSq = dx * dx + dy * dy;
                if (distSq < REPEL_RADIUS * REPEL_RADIUS) {
                    const dist = Math.sqrt(distSq) || 0.001;
                    const force = (1 - dist / REPEL_RADIUS) * REPEL_STRENGTH;
                    particle.x += (dx / dist) * force;
                    particle.y += (dy / dist) * force;
                }
            }

            // Bounce off the intro text's bounding boxes like solid walls.
            obstacles.forEach((rect) => resolveObstacleCollision(particle, rect));

            // Wrap gently around the edges so shapes keep drifting forever.
            const margin = particle.size;
            if (particle.x < -margin) particle.x = width + margin;
            if (particle.x > width + margin) particle.x = -margin;
            if (particle.y < -margin) particle.y = height + margin;
            if (particle.y > height + margin) particle.y = -margin;

            // Advance the staggered pop-in: wait out the delay, then ease
            // opacity/scale up to full over APPEAR_FRAMES frames.
            if (particle.appearProgress < 1) {
                if (particle.appearDelay > 0) {
                    particle.appearDelay -= 1;
                } else {
                    particle.appearProgress = Math.min(1, particle.appearProgress + 1 / APPEAR_FRAMES);
                }
            }
            const appearScale = (particle.appearProgress < 1 ? Math.max(0, easeOutBack(particle.appearProgress)) : 1) * shrinkScale;
            const appearOpacity = (particle.appearProgress < 1 ? easeOutCubic(particle.appearProgress) : 1) * shrinkScale;

            ctx.save();
            ctx.translate(particle.x, particle.y);
            ctx.rotate(particle.rotation);
            ctx.scale(appearScale, appearScale);
            ctx.globalAlpha = particle.opacity * appearOpacity;
            ctx.fillStyle = colors[particle.colorIndex];
            ctx.strokeStyle = colors[particle.colorIndex];
            drawShape(ctx, particle, halloweenActive ? colors[particle.colorIndex] : null);
            ctx.restore();
        });

        animationFrame = requestAnimationFrame(step);
    };

    const start = () => {
        if (animationFrame) return;
        animationFrame = requestAnimationFrame(step);
    };

    const stop = () => {
        if (!animationFrame) return;
        cancelAnimationFrame(animationFrame);
        animationFrame = null;
    };

    readColors();
    resize(true);
    start();

    window.addEventListener('resize', handleResize);

    // Track pointer position (mouse, pen, or touch) relative to the
    // container so particles can be pushed away from it in step(). The
    // container itself is `pointer-events: none` (it sits behind the header
    // content), so listen on the window and convert to local coordinates.
    const updatePointer = (event) => {
        const rect = container.getBoundingClientRect();
        pointer.x = event.clientX - rect.left;
        pointer.y = event.clientY - rect.top;
        pointer.active = true;
    };
    const clearPointer = () => {
        pointer.active = false;
    };
    window.addEventListener('pointermove', updatePointer, { passive: true });
    window.addEventListener('pointerdown', updatePointer, { passive: true });
    window.addEventListener('pointerleave', clearPointer);
    document.addEventListener('mouseleave', clearPointer);

    // Pause the animation while the section is scrolled out of view, or the
    // tab is hidden, to keep things cheap.
    const observer = new IntersectionObserver((entries) => {
        isVisible = entries.some((entry) => entry.isIntersecting);
        if (isVisible && document.visibilityState === 'visible') {
            start();
        } else {
            stop();
        }
    });
    observer.observe(container);

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible' && isVisible) {
            start();
        } else {
            stop();
        }
    });

    // Re-read the colour palette whenever dark mode is toggled.
    new MutationObserver(readColors).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class'],
    });

    // If the user's motion preference changes at runtime, stop animating.
    prefersReducedMotion.addEventListener?.('change', (event) => {
        if (event.matches) {
            stop();
            ctx.clearRect(0, 0, width, height);
        } else {
            start();
        }
    });
};

export default initParticles;
